import { useState, useEffect } from 'react';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import InputBase from '@mui/material/InputBase';
import { Search } from 'functions/src/types/firestore/Search';
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { SearchQuery } from 'functions/src/types/firestore/SearchQuery';
import { SearchResultCard } from '../cards/SearchResultCard';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import { GradientIcon } from '../gradients/GradientIcon';
import { useDebounce } from '../../hooks/useDebounce';
import { TAG_CHILDREN_MAP } from './tags';
import { useQueryCache } from '../../hooks/useQueryCache';
import { SearchTagsSection } from './SearchTagsSection';
import { SelectTagsSection } from './SelectTagsSection';
import { AutoCompleteSection } from './AutoCompleteSection';
import {
  addSearchQuery,
  getGlobalQueries,
  pageSearch,
} from '../../firebaseCloud';
import { useSearch } from 'src/contexts/SearchContext';
type SearchResultsProps = {
  onClose: () => void;
};

type CachedResult = Search;

export const SearchResults = ({ onClose }: SearchResultsProps) => {
  const theme = useTheme();
  const [searchResults, setSearchResults] = useState<Search[]>([]);
  const [timespan, setSearchTimespan] = useState<string>('0');
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [cachedResults, setCachedResults] = useState<CachedResult[]>([]);
  const [globalQueries, setGlobalQueries] = useState<SearchQuery[]>([]);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const debouncedSelectedTags = useDebounce(selectedTags, 500);
  const { setSearchQuery, searchQuery } = useSearch();
  // eslint-disable-next-line @blumintinc/blumint/extract-global-constants
  const handleClear = () => {
    setSearchQuery('');
    setSearchResults([]);
    setSelectedTags([]);
    onClose();
  };
  // eslint-disable-next-line @blumintinc/blumint/extract-global-constants
  const handleTagUpdate = (tag: string) => {
    if (selectedTags.includes(tag)) {
      const filtered = selectedTags.filter((t) => {
        return t !== tag;
      });
      setSelectedTags(filtered);
    } else {
      setSelectedTags([...selectedTags, tag]);
    }
  };

  const debouncedSearchQuery = useDebounce(searchQuery, 500);
  useQueryCache(
    addSearchQuery,
    setCachedResults,
    setSuggestions,
    debouncedSearchQuery,
  );
  useEffect(() => {
    // eslint-disable-next-line @blumintinc/blumint/extract-global-constants
    const handler = async () => {
      const { data } = await getGlobalQueries();
      if (data?.globalQueries.length > 0) {
        setGlobalQueries(data.globalQueries as SearchQuery[]);
      }
    };
    handler();
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchQuery]);

  useEffect(() => {
    if (!debouncedSearchQuery) {
      return;
    }
    const fetchData = async () => {
      const t0 = Date.now();
      const { data } = await pageSearch({
        searchQuery: debouncedSearchQuery,
        selectedTags: debouncedSelectedTags.filter((tag) => {
          if (
            TAG_CHILDREN_MAP[`${tag}`] &&
            TAG_CHILDREN_MAP[`${tag}`].some((child) => {
              return debouncedSelectedTags.includes(child);
            })
          ) {
            return false;
          }
          return true;
        }),
      });
      if (data) {
        setSearchResults(data.hits as Search[]);
        const t1 = Date.now();
        setSearchTimespan(((t1 - t0) / 1000).toFixed(2));
      }
    };
    fetchData();
  }, [debouncedSearchQuery, debouncedSelectedTags]);

  return (
    <ClickAwayListener onClickAway={onClose}>
      <Box sx={{ position: 'relative', top: 0, left: 0 }}>
        <Box
          sx={{
            position: 'absolute',
            zIndex: theme.zIndex.appBar,
            top: -20,
            left: 0,
            width: 450,
            maxHeight: '60vh',
            overflowY: 'auto',
            background: theme.palette.background.elevation[8],
            border: `1px ${theme.palette.primary.main} solid`,
            borderRadius: '20px',
            px: 2,
            py: 1,
            // backdrop filter needs to be on pseudo element to apply to elements outside the parent
            '&::before': {
              content: '""',
              position: 'absolute',
              height: '100%',
              width: '100%',
              backdropFilter: theme.glass[8],
              zIndex: -1,
              top: 0,
              left: 0,
            },
            boxShadow: theme.shadows[4],
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 1,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              <SearchIcon />
              <InputBase
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                }}
                color="primary"
                autoFocus
                placeholder="Search..."
                sx={{
                  flex: 1,
                  color: 'white',
                  ml: 1,
                  height: '100%',
                  width: '100%',
                }}
              />
            </Box>
            <IconButton sx={{ width: 24, height: 24 }} onClick={handleClear}>
              <GradientIcon IconComponent={CloseIcon} />
            </IconButton>
          </Box>
          <SearchTagsSection
            selectedTags={selectedTags}
            handleTagUpdate={handleTagUpdate}
          />
          <AutoCompleteSection
            searchQuery={searchQuery}
            globalQueries={globalQueries}
            suggestions={suggestions}
            cachedResults={cachedResults}
            setSearchQuery={setSearchQuery}
          />
          <SelectTagsSection
            selectedTags={selectedTags}
            handleTagUpdate={handleTagUpdate}
          />
          {searchQuery && searchResults?.length > 0 ? (
            <>
              <Divider
                sx={{
                  my: 1,
                  //background:
                  //  'radial-gradient(50% 50% at 50% 50%, rgba(255, 255, 255, 0) 0%, #FFFFFF 49.22%, rgba(255, 255, 255, 0) 100%)',
                  width: 'calc(100%)',
                  //transform: 'matrix(-1, 0, 0, 1, 0, 0)',
                }}
              />
              <Box>
                <Typography
                  sx={{ color: theme.palette.text.secondary, mb: 3, ml: 0.5 }}
                >{`Exactly ${searchResults?.length} Results (${timespan} seconds)`}</Typography>
                {searchResults.map((searchResult) => {
                  return (
                    <Box key={searchResult?.id} sx={{ mb: 2 }}>
                      <SearchResultCard searchResult={searchResult} />
                    </Box>
                  );
                })}
              </Box>
            </>
          ) : (
            ''
          )}
        </Box>
      </Box>
    </ClickAwayListener>
  );
};
